// Hooks du module Workflows (WKF-002) : stats + mutations de pilotage.
//
// Le LISTING passe par <DataTable mode="server" endpoint="/api/workflows"> qui
// gère son propre cache. Après une mutation d'état/suppression, on invalide ce
// cache via `invalidateDataTableListings` + la clé `workflows.stats()`.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { BulkImpact } from '@/components/ui/data-table/actions/action-types'
import { apiFetch, API_BASE } from '@/lib/api'
import { extractStructuredApiError } from '@/lib/api-error'
import { toBulkImpact, type BulkImpactPayload } from '@/lib/bulk-impact'
import { invalidateDataTableListings } from '@/lib/datatable-invalidate'
import { queryKeys } from '@/lib/queryKeys'

import { langueDuRefus } from './useWorkflowEditor.shared'

import type {
    NativeCatalog,
    WorkflowAiDraft,
    WorkflowBulkActionKind,
    WorkflowBulkResult,
    WorkflowStats,
} from './useWorkflows.types'

const WORKFLOWS_BASE = `${API_BASE}/workflows`
const LISTING_ENDPOINT = '/api/workflows'

function headers(entityId: string): HeadersInit {
    return { 'Content-Type': 'application/json', 'X-Entity-Id': entityId }
}

async function readError(resp: Response): Promise<Error> {
    let payload: unknown = {}
    try {
        payload = await resp.json()
    } catch {
        /* ignore */
    }
    const { message, code } = extractStructuredApiError(payload)
    const err = new Error(message || `Erreur ${resp.status}`) as Error & { code?: string }
    err.code = code && code !== 'UNKNOWN' ? code : `HTTP_${resp.status}`
    return err
}

export function useWorkflowStats(entityId: string) {
    return useQuery<WorkflowStats>({
        queryKey: queryKeys.workflows.stats(entityId),
        enabled: !!entityId,
        queryFn: async () => {
            const resp = await apiFetch(`${WORKFLOWS_BASE}/stats?entity_id=${entityId}`, {
                headers: headers(entityId),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json()
        },
    })
}

function useInvalidateWorkflows() {
    const qc = useQueryClient()
    return () => {
        invalidateDataTableListings(qc, LISTING_ENDPOINT)
        void qc.invalidateQueries({ queryKey: queryKeys.workflows.statsAll() })
        // Les deux galeries (pilotage du menu, personnalisation des Réglages)
        // montrent le même état actif/en pause : un geste fait ailleurs doit s'y
        // voir immédiatement. On invalide la racine, donc les deux variantes.
        void qc.invalidateQueries({ queryKey: queryKeys.workflows.nativesAll() })
    }
}

/** Catalogue des automatisations groupé par thème (WKF-012 §6.1).
 *  `includeCustom` : la vue de pilotage du menu montre TOUT ce que l'organisme a
 *  (sur Focus/Boost c'est son seul écran) ; la galerie des Réglages ne montre que
 *  les modèles Argalis, puisque les créations vivent dans le tableau voisin. */
export function useWorkflowNatives(entityId: string, includeCustom = false) {
    return useQuery<NativeCatalog>({
        queryKey: queryKeys.workflows.natives(entityId, includeCustom),
        enabled: !!entityId,
        queryFn: async () => {
            const resp = await apiFetch(
                `${WORKFLOWS_BASE}/natives?entity_id=${entityId}&include_custom=${includeCustom}`,
                { headers: headers(entityId) },
            )
            if (!resp.ok) throw await readError(resp)
            return resp.json()
        },
    })
}

/** Bascule Actif/Pause (idempotent). */
export function useToggleWorkflow(entityId: string) {
    const invalidate = useInvalidateWorkflows()
    return useMutation({
        mutationFn: async ({ id, action }: { id: string; action: 'activate' | 'pause' }) => {
            const resp = await apiFetch(
                `${WORKFLOWS_BASE}/${id}/activate?entity_id=${entityId}&${langueDuRefus()}`,
                { method: 'PATCH', headers: headers(entityId), body: JSON.stringify({ action }) },
            )
            if (!resp.ok) throw await readError(resp)
            // `relieved_model` : nom du modèle Argalis mis en pause par la RELÈVE
            // (activer une version personnalisée suspend son modèle d'origine).
            return resp.json() as Promise<{
                id: string
                status: string
                relieved_model?: string | null
            }>
        },
        onSuccess: invalidate,
    })
}

/** Refus — ou reprise — d'un modèle HÉRITÉ sur les données de l'entité courante.
 *
 *  Ce n'est PAS une mise en pause : le modèle appartient à une entité au-dessus et
 *  continue de tourner chez elle et chez ses autres entités rattachées. Seule
 *  l'entité courante s'en soustrait (`workflow_entity_optouts`, mig 470). */
export function useWorkflowEntityOptOut(entityId: string) {
    const invalidate = useInvalidateWorkflows()
    return useMutation({
        mutationFn: async ({
            id,
            optedOut,
            reason,
        }: {
            id: string
            optedOut: boolean
            reason?: string
        }) => {
            const resp = await apiFetch(
                `${WORKFLOWS_BASE}/${id}/entity-optout?entity_id=${entityId}`,
                {
                    method: 'PATCH',
                    headers: headers(entityId),
                    body: JSON.stringify({ opted_out: optedOut, reason: reason ?? null }),
                },
            )
            if (!resp.ok) throw await readError(resp)
            return resp.json() as Promise<{
                workflow_id: string
                name: string
                owner_entity_name: string | null
                is_opted_out: boolean
            }>
        },
        onSuccess: invalidate,
    })
}

/** Duplication (modèle OU workflow maison → copie éditable en brouillon).
 *  Ne touche jamais à la source : la mise en pause du modèle d'origine se fait à
 *  l'ACTIVATION de la copie (voir `useToggleWorkflow`). */
export function useDuplicateWorkflow(entityId: string) {
    const invalidate = useInvalidateWorkflows()
    return useMutation({
        mutationFn: async ({ id, name }: { id: string; name?: string }) => {
            const resp = await apiFetch(`${WORKFLOWS_BASE}/${id}/duplicate?entity_id=${entityId}`, {
                method: 'POST',
                headers: headers(entityId),
                body: JSON.stringify({ name }),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json() as Promise<{ id: string; name: string }>
        },
        onSuccess: invalidate,
    })
}

/** Génération d'une proposition à partir d'une phrase (WKF-016 §6).
 *
 *  MUTATION, jamais une requête en cache : chaque génération est un ACTE, avec son
 *  coût. Deux clics sur « Générer » avec la même phrase doivent produire deux appels
 *  — l'utilisateur qui recommence attend une nouvelle tentative, pas la précédente.
 *
 *  Rien n'est persisté : la réponse est un brouillon en mémoire. C'est l'éditeur,
 *  ensuite, qui décide d'écrire — donc aucune invalidation ici. */
export function useWorkflowAiDraft(entityId: string) {
    return useMutation({
        mutationKey: queryKeys.workflows.aiDraft(),
        mutationFn: async (description: string) => {
            const resp = await apiFetch(`${WORKFLOWS_BASE}/ai/draft?entity_id=${entityId}`, {
                method: 'POST',
                headers: headers(entityId),
                body: JSON.stringify({ description }),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json() as Promise<WorkflowAiDraft>
        },
    })
}

/** Suppression (soft-delete gardé : natif → 409, exécutions → 422). */
export function useDeleteWorkflow(entityId: string) {
    const invalidate = useInvalidateWorkflows()
    return useMutation({
        mutationFn: async (id: string) => {
            const resp = await apiFetch(`${WORKFLOWS_BASE}/${id}?entity_id=${entityId}`, {
                method: 'DELETE',
                headers: headers(entityId),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json()
        },
        onSuccess: invalidate,
    })
}

/**
 * Aperçu d'impact d'une action groupée — **n'écrit rien**.
 *
 * Passe par la route de masse EXISTANTE (`?dry_run=true`), jamais par une route
 * d'aperçu dédiée : une seconde route serait un second endroit où l'éligibilité
 * peut diverger de l'exécution (convention `bulk-action-impact-preview`).
 *
 * Pas de `useMutation` : l'appel part à l'ouverture du dialogue de
 * confirmation, piloté par `useBulkImpact` côté socle, et n'a ni cache ni état
 * global à tenir.
 */
export async function previewBulkWorkflows(
    entityId: string,
    ids: string[],
    action: WorkflowBulkActionKind,
): Promise<BulkImpact> {
    const resp = await apiFetch(`${WORKFLOWS_BASE}/bulk?entity_id=${entityId}&dry_run=true`, {
        method: 'POST',
        headers: headers(entityId),
        body: JSON.stringify({ ids, action }),
    })
    if (!resp.ok) throw await readError(resp)
    return toBulkImpact((await resp.json()) as BulkImpactPayload, 'workflows.bulk.reason')
}

/** Actions groupées (activer / pause / supprimer) — résultat partiel toléré. */
export function useBulkWorkflows(entityId: string) {
    const invalidate = useInvalidateWorkflows()
    return useMutation({
        mutationFn: async ({ ids, action }: { ids: string[]; action: WorkflowBulkActionKind }) => {
            const resp = await apiFetch(`${WORKFLOWS_BASE}/bulk?entity_id=${entityId}`, {
                method: 'POST',
                headers: headers(entityId),
                body: JSON.stringify({ ids, action }),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json() as Promise<WorkflowBulkResult>
        },
        onSuccess: invalidate,
    })
}
